import { EventEmitter } from 'events';
import { vec2 } from 'gl-matrix';
import {
    BrushOptions,
    DeformOptions,
    LineDeformOptions,
    LineOptions,
} from '~/state/atoms/toolsAtoms';
import { Environment } from '../data/Environment';
import { ImageObject } from '../data/image/ImageObject';
import { RegionOfInterest } from '../data/structure/RegionOfInterest';
import { PanZoom } from '../view/PanZoom';
import { View } from '../view/View';
import { ViewManager } from '../view/ViewManager';
import { PlaneView } from '../view/views/PlaneView';
import { ScrollAction } from './actions/ScrollAction';
import { ZoomAction } from './actions/ZoomAction';
import { GlobalCoordinator } from './coordinators/GlobalCoordinator';
import { GlobalPanCoordinator } from './coordinators/GlobalPanCoordinator';
import { GlobalWindowingCoordinator } from './coordinators/GlobalWindowingCoordinator';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from './EventHandlerResult';
import { ModifierKeys } from './ModifierKeys';
import { CrosshairInteractionMode, Tool } from './Tool';
import { BoxZoomTool } from './tools/BoxZoomTool';
import { AdaptiveBrushTool } from './tools/contouring-tools/AdaptiveBrushTool';
import { LineDrawTool } from './tools/contouring-tools/LineDrawTool';
import { DeformTool } from './tools/contouring-tools/DeformTool';
import { LineDeformTool } from './tools/contouring-tools/LineDeformTool';
import { StaticBrushTool } from './tools/contouring-tools/StaticBrushTool';
import { TransformTool } from './tools/contouring-tools/TransformTool';
import { PanTool } from './tools/PanTool';
import { RegistrationMode, RegistrationTool } from './tools/RegistrationTool';

// ───────── ToolConfig discriminated union ─────────

/** Shared fields required by every tool configuration. */
interface BaseToolConfig {
    panZoom: PanZoom;
    views: PlaneView[] | undefined;
    environment: Environment | undefined;
}

interface PanToolConfig extends BaseToolConfig {
    tool: 'pan';
}

interface BrushToolConfig extends BaseToolConfig {
    tool: 'brush';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: BrushOptions;
    onBrushSizeChanged: (diameter: number) => void;
}

interface LineToolConfig extends BaseToolConfig {
    tool: 'line';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: LineOptions;
    drawingView: PlaneView | undefined;
}

interface RegistrationToolConfig extends BaseToolConfig {
    tool: 'registration';
    mode: RegistrationMode | null;
}

interface BoxZoomToolConfig extends BaseToolConfig {
    tool: 'boxZoom';
}

interface TransformToolConfig extends BaseToolConfig {
    tool: 'transform';
    structure: RegionOfInterest | undefined;
    siblingSupports?: RegionOfInterest[];
}

interface LineDeformToolConfig extends BaseToolConfig {
    tool: 'lineDeform';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: LineDeformOptions;
    onSpreadChanged: (spreadPercent: number) => void;
}

interface DeformToolConfig extends BaseToolConfig {
    tool: 'deform';
    structure: RegionOfInterest | undefined;
    image: ImageObject | undefined;
    options: DeformOptions;
    onDiameterChanged: (diameter: number) => void;
}
export { CrosshairInteractionMode } from './Tool';

/**
 * Discriminated union for {@link ToolManager.setActiveTool}.
 */
export type ToolConfig =
    | PanToolConfig
    | BrushToolConfig
    | LineToolConfig
    | RegistrationToolConfig
    | BoxZoomToolConfig
    | TransformToolConfig
    | LineDeformToolConfig
    | DeformToolConfig;
/**
 * {@link ToolManager} is responsible for handling the user actions.
 *
 * {@link HTMLCanvasElement Canvas} catches the action user performs in the form of an event.\
 * It then passes that event to {@link ToolManager}.
 *
 * {@link ToolManager} then propagates that event through the cycle of possible {@link EventHandler EventHandlers}.\
 * The event is propagated in the following order:\
 * {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} and
 * {@link ToolManager.activeView activeView}. In case of {@link ToolManager.onKeyPress KeyEvents}, we have one more option, {@link ToolManager.sharedKeyEvents sharedKeyEvents}.
 *
 * {@link ToolManager} hands out the event to {@link EventHandler}.\
 * {@link EventHandler} inspects if it can handle the event and what to do with it.\
 * {@link EventHandler} then returns a {@link EventHandlerResult response} to {@link ToolManager} telling how to proceed:
 *
 * {@link EventHandler} may have started a new {@link ToolAction action}, in which case {@link ToolManager} should set that action as {@link ToolManager.activeAction active}.\
 * {@link EventHandler} may have ended an {@link ToolAction action}, in which case the {@link ToolManager}'s {@link ToolManager.activeAction active action} should be removed.\
 * {@link EventHandler} also informs whether the event was successfully handled or not,\
 * and whether the event should be propagated further or not.
 *
 * {@link ToolManager} then either proceeds the propagation further or exits the event handling.
 */
export class ToolManager {
    /**
     * {@link ToolManager} singleton. There may only ever be one active ToolManager at any given moment.
     */
    private static readonly toolManager = new ToolManager();

    /**
     * {@link EventEmitter} to emit events.
     * This is used to emit events that are not directly related to the {@link ToolManager}.
     * Such as the {@link ViewManager.viewUpdateEvent viewUpdateEvent}.
     */
    private static eventEmitter = new EventEmitter();

    /**
     * The currently active Tool.\
     * This is used in event propagation.
     */
    private activeTool?: Tool;

    /**
     * Coordinator for all global pan state and logic
     * (middle-button pan, key+drag pan, palm cursor).
     */
    private panCoordinator = new GlobalPanCoordinator();

    /**
     * Coordinator for global right-click-drag windowing.
     */
    private windowingCoordinator = new GlobalWindowingCoordinator();

    /** All global coordinators, for uniform lifecycle dispatch. */
    private readonly coordinators: readonly GlobalCoordinator[] = [
        this.panCoordinator,
        this.windowingCoordinator,
    ];

    /**
     * Global {@link ZoomAction} bound to Ctrl+Wheel.
     * Tried AFTER the active tool so that custom wheel
     * behaviors (e.g. Shift+Wheel brush-size) take priority.
     */
    private globalZoomAction: ZoomAction | undefined;

    /**
     * Global {@link ScrollAction} for slice navigation.
     * Tried AFTER the active tool and global zoom so that
     * tool-specific and zoom wheel behaviors take priority.
     */
    private globalScrollAction: ScrollAction | undefined;

    /** True while the user is dragging a crosshair overlay element.
     *  Prevents canvas-level tool actions from processing mousemove
     *  events that leak through when the cursor drifts off the SVG line. */
    private static _isCrosshairDragActive = false;

    /** Whether the active tool has been activated (onActivate called).
     *  Prevents redundant onActivate calls when onMouseEnter fires
     *  immediately after setActiveTool. */
    private static _isToolActivated = false;

    /** External crosshair mode set by non-tool UI (e.g. POI move overlay).
     *  null means "no override — let tool-based logic decide".
     *
     *  TODO: Remove once POI Move is a proper {@link Tool}.  At that
     *  point its crosshair mode should be declared via the virtual
     *  method described in {@link _getToolBasedCrosshairMode}, and
     *  this escape hatch (along with {@link setExternalCrosshairMode})
     *  can be deleted. */
    private static _externalCrosshairMode: CrosshairInteractionMode | null =
        null;

    private constructor() {}

    /**
     * Method to handle the EventHandlerResult.
     * @param result EventHandlerResult that should be handled
     * @returns true if the event should stop propagating, false to continue propagation further.
     */
    private handleEventResult(result: EventHandlerResult): boolean {
        if (result.viewUpdateEvent) {
            ViewManager.viewUpdateEvent(result.viewUpdateEvent);
        }
        return result.stopPropagation;
    }

    /**
     * Returns true when a drawing action is active in a specific view
     * and the given view is a different view. Used to block pan in
     * viewports that are not the drawing viewport during an action.
     * Compares by label and type rather than object identity to
     * handle cases where viewport wrapper objects differ between
     * construction time and event dispatch time.
     */
    private isDrawingActiveInOtherView(view: View): boolean {
        if (!(view instanceof PlaneView)) {
            return false;
        }
        const drawingView = this.activeTool?.getDrawingView();
        if (drawingView === undefined) {
            return false;
        }
        return !(
            view.label === drawingView.label && view.type === drawingView.type
        );
    }

    /**
     * Create a {@link Tool} instance from a fully-typed
     * {@link ToolConfig}. Returns `null` when required data
     * (structure, image, transversal view) is missing.
     */
    private createToolForConfig = (config: ToolConfig): Tool | null => {
        switch (config.tool) {
            case 'pan':
                return new PanTool();
            case 'boxZoom':
                return new BoxZoomTool(config.panZoom);
            case 'brush': {
                const { structure, image, options } = config;
                if (!structure || !image) {
                    return null;
                }
                switch (options.mode) {
                    case 'static':
                        return new StaticBrushTool(
                            image,
                            structure,
                            options.erase,
                            options.diameter,
                            config.onBrushSizeChanged
                        );
                    case 'smart':
                        return new AdaptiveBrushTool(
                            image,
                            structure,
                            options.erase,
                            options.diameter,
                            config.onBrushSizeChanged
                        );
                    default:
                        return null;
                }
            }

            case 'line': {
                const { structure, image, drawingView, options } = config;
                if (!structure || !image || !drawingView) {
                    return null;
                }
                if (options.mode === 'draw' || options.mode === 'erase') {
                    return new LineDrawTool(
                        image,
                        structure,
                        options.mode,
                        drawingView
                    );
                }
                return null;
            }

            case 'registration':
                return new RegistrationTool(
                    config.mode,
                    config.views,
                    config.environment
                );
            case 'transform': {
                const { structure, views, siblingSupports } = config;
                if (structure && views) {
                    return new TransformTool(structure, views, siblingSupports);
                }
                return null;
            }
            case 'lineDeform': {
                const { structure, image, options } = config;
                if (!structure || !image) {
                    return null;
                }
                return new LineDeformTool(
                    image,
                    structure,
                    options.spreadPercent,
                    config.onSpreadChanged
                );
            }
            case 'deform': {
                const { structure, image, options } = config;
                if (!structure || !image) {
                    return null;
                }
                return new DeformTool(
                    image,
                    structure,
                    options.diameter,
                    config.onDiameterChanged
                );
            }
        }
    };

    /**
     * Set the active tool from a fully-typed {@link ToolConfig}.
     * Deactivates the current tool, creates the new one, and
     * (re-)initialises global coordinators and actions.
     */
    public static setActiveTool(config: ToolConfig) {
        this.withStateNotification(() => {
            const newActiveTool = this.toolManager.createToolForConfig(config);
            // Always dispose the previous tool
            this.deactivateAndDisposeTool();
            if (newActiveTool) {
                this.toolManager.activeTool = newActiveTool;
                this.toolManager.panCoordinator.initialize(
                    config.panZoom,
                    config.tool === 'pan',
                    config.views
                );
                this.toolManager.windowingCoordinator.initialize(
                    config.environment,
                    config.views
                );
                this.toolManager.globalZoomAction = new ZoomAction(
                    config.panZoom,
                    ModifierKeys.CTRL
                );
                this.toolManager.globalScrollAction = new ScrollAction();
                this.activateActiveTool();
            } else {
                this.toolManager.activeTool = undefined;
            }
        });
    }

    /**
     * Deactivates and clears the current active tool.
     * Use when the viewport is not ready or when navigating
     * away from a step to ensure no stale tool remains active.
     */
    public static deactivateAndClearTool() {
        this.withStateNotification(() => {
            this.deactivateAndDisposeTool();
            this.toolManager.activeTool = undefined;
        });
    }

    /**
     * Method to activate the active tool
     */
    private static activateActiveTool() {
        if (this._isToolActivated) {
            return;
        }
        this._isToolActivated = true;
        const result = this.toolManager.activeTool?.onActivate();
        if (result) {
            this.toolManager.handleEventResult(result);
        }
        // Apply the tool's custom cursor on activation so a tool or mode
        // change is reflected immediately, without waiting for a mouse move.
        this.toolManager.activeTool?.refreshCursor();
    }

    /**
     * Method to deactivate the active tool
     */
    private static deactivateActiveTool() {
        this._isToolActivated = false;
        const result = this.toolManager.activeTool?.onDeactivate();
        if (result) {
            this.toolManager.handleEventResult(result);
        }
    }

    /**
     * Deactivate *and* dispose the current tool.  Called when the
     * tool instance is being permanently replaced or removed so
     * that long-lived resources (gizmos, etc.) are cleaned up.
     */
    private static deactivateAndDisposeTool() {
        this.deactivateActiveTool();
        this.toolManager.activeTool?.dispose();
    }

    /**
     * Method to get the active tool
     */
    public static getActiveTool(): Tool | undefined {
        return this.toolManager.activeTool;
    }

    /** The view the active tool is currently drawing in, or undefined. */
    public static getActiveToolDrawingView(): PlaneView | undefined {
        return this.toolManager.activeTool?.getDrawingView();
    }

    /**
     * Returns the current {@link CrosshairInteractionMode} for the
     * Crosshairs component rendered in the given view.
     *
     * The result drives two independent decisions in the component:
     *   1. Which hit areas are interactive (line bodies, root handles,
     *      centre handle).
     *   2. Whether a visual "disabled" indicator is shown on the lines.
     *
     * The {@link Crosshairs} component subscribes to the
     * `toolStateChanged` event via {@link useCrosshairInteraction} and
     * updates hit-area `pointer-events` and visuals accordingly.
     *
     * @param view The PlaneView rendered by the Crosshairs component.
     */
    public static getCrosshairInteractionMode(
        view: PlaneView | null
    ): CrosshairInteractionMode {
        const manager = this.toolManager;
        const activeTool = manager.activeTool;

        // Compute the mode driven solely by the active tool, ignoring
        // global pan/windowing state.  We need this first so that we can
        // preserve the visual when global actions are active.
        const toolMode = ToolManager._getToolBasedCrosshairMode(
            manager,
            view,
            activeTool
        );

        // Non-tool UI override (e.g. POI move): apply when the tool
        // hasn't suppressed crosshairs itself.
        if (
            toolMode === CrosshairInteractionMode.Full &&
            this._externalCrosshairMode !== null
        ) {
            return this._externalCrosshairMode;
        }

        // Global pan or windowing active: disable interaction but keep
        // the visual stable so the crosshairs do not flash between
        // active and disabled appearances while the user is panning or
        // adjusting windowing.
        if (
            manager.panCoordinator.isActive ||
            manager.windowingCoordinator.isActive ||
            manager.panCoordinator.isPanKeyHeld
        ) {
            const showsDisabledVisual =
                toolMode === CrosshairInteractionMode.RootsOnly ||
                toolMode === CrosshairInteractionMode.DrawingStroke;
            return showsDisabledVisual
                ? CrosshairInteractionMode.DrawingStroke
                : CrosshairInteractionMode.None;
        }

        return toolMode;
    }

    /**
     * Computes the crosshair interaction mode based purely on the
     * active tool state, without taking global pan / windowing into
     * account.  Delegates to {@link Tool.getCrosshairInteractionMode}
     * on the active tool — each tool owns its own policy.
     */
    private static _getToolBasedCrosshairMode(
        manager: ToolManager,
        view: PlaneView | null,
        activeTool: Tool | undefined
    ): CrosshairInteractionMode {
        if (!activeTool) {
            return CrosshairInteractionMode.Full;
        }
        const isDrawingActiveInOtherView =
            view !== null && manager.isDrawingActiveInOtherView(view);
        return activeTool.getCrosshairInteractionMode(
            isDrawingActiveInOtherView
        );
    }

    public static setCrosshairDragActive(active: boolean): void {
        this._isCrosshairDragActive = active;
    }

    public static setExternalCrosshairMode(
        mode: CrosshairInteractionMode | null
    ): void {
        this._externalCrosshairMode = mode;
        this.eventEmitter.emit('toolStateChanged');
    }

    /**
     * Wraps a state-changing operation so that a generic
     * `toolStateChanged` event is emitted after it completes.
     * Consumers (e.g. {@link useCrosshairInteraction}) subscribe
     * to this event and derive their own state; `useSyncExternalStore`
     * deduplicates so only actual changes cause re-renders.
     */
    private static withStateNotification<T>(fn: () => T): T {
        try {
            return fn();
        } finally {
            this.eventEmitter.emit('toolStateChanged');
        }
    }

    /**
     * Method to get the {@link ToolManager.eventEmitter eventEmitter}.\
     */
    public static getEventEmitter(): EventEmitter {
        return this.eventEmitter;
    }

    /**
     * {@link ToolManager.onKeyPress KeyEvents} that are not subjective to specific {@link ToolAction}, {@link Tool} or {@link View}.\
     * Such as undo (Ctrl+Z) and redo (Ctrl + Y).
     * @param key The key that was pressed
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the key press
     * @returns An {@link EventHandlerResult} of type {@link EventHandlerResultType.EVENT_NOT_HANDLED EVENT_NOT_HANDLED}, if the event couldn't be handled
     * {@link EventHandlerResult} of any other type if the event was succesfully handled.
     */
    private sharedKeyEvents(
        key: string,
        modifierKeys: ModifierKeys
    ): EventHandlerResult {
        switch (key) {
            case 'z':
                if (modifierKeys.equals(ModifierKeys.CTRL)) {
                    //TODO: To be reintroduced
                    /*if(StateManager.undo()){
                        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
                    }*/
                }
                break;
            case 'y':
                if (modifierKeys.equals(ModifierKeys.CTRL)) {
                    //TODO: To be reintroduced
                    /*if(StateManager.redo()){
                        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
                    }*/
                }
                break;
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    /**
     * Mouse press event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMousePress(
        button: number,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: View
    ): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;
            this.eventEmitter.emit('onMousePress', {
                button,
                modifierKeys,
                coordinates,
                view,
            });

            // Block all mouse presses while windowing is active
            if (manager.windowingCoordinator.isActive) {
                return true;
            }

            // Global pan (key+drag or middle mouse button)
            if (
                !manager.isDrawingActiveInOtherView(view) &&
                manager.panCoordinator.onMousePress(
                    button,
                    modifierKeys,
                    coordinates,
                    view,
                    () => manager.activeTool?.isStrokeInProgress() ?? false,
                    (result) => manager.handleEventResult(result)
                )
            ) {
                return true;
            }

            // Global windowing (right-click-drag)
            if (
                view instanceof PlaneView &&
                manager.windowingCoordinator.onMousePress(
                    button,
                    coordinates,
                    view,
                    modifierKeys,
                    () => manager.activeTool?.isStrokeInProgress() ?? false,
                    manager.panCoordinator.isActive,
                    (btn: number) =>
                        manager.activeTool?.usesButtonDuringAction(btn) ?? false
                )
            ) {
                return true;
            }
            //if it fails,
            if (manager.activeTool) {
                //let the active tool handle it
                if (
                    manager.handleEventResult(
                        manager.activeTool.onMousePress(
                            button,
                            modifierKeys,
                            coordinates,
                            view
                        )
                    )
                ) {
                    return true;
                }
            }
            //if that fails also,
            if (view && view.onMousePress) {
                //let the view try to handle it
                return manager.handleEventResult(
                    view.onMousePress(button, modifierKeys, coordinates, view)
                );
            }
            //if none is able to handle the event, we just return
            //false to inform that the event couldn't be handled.
            return false;
        });
    }

    /**
     * Mouse move event triggered by {@link ViewManager}.
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseMove(
        coordinates: vec2,
        buttons: number,
        view: View,
        movement?: vec2
    ): boolean {
        if (this._isCrosshairDragActive) {
            return false;
        }

        const manager = this.toolManager;

        // Global pan (key-pan resume + middle mouse button)
        if (
            !manager.isDrawingActiveInOtherView(view) &&
            manager.panCoordinator.onMouseMove(
                coordinates,
                buttons,
                view,
                () => manager.activeTool?.isStrokeInProgress() ?? false,
                (result) => manager.handleEventResult(result)
            )
        ) {
            // Update active tool visual state during pan
            if (manager.activeTool) {
                manager.activeTool.onViewChanged(coordinates, view);
                manager.panCoordinator.refreshPanCursor();
            }
            return true;
        }

        // Global windowing (right-click-drag)
        if (
            movement &&
            manager.windowingCoordinator.onMouseMove(movement, buttons)
        ) {
            return true;
        }

        if (manager.activeTool) {
            const isStrokeActive = () =>
                manager.activeTool?.isStrokeInProgress() ?? false;
            const toolHandled = manager.handleEventResult(
                manager.activeTool.onMouseMove(coordinates, buttons, view)
            );
            // Re-assert palm cursor after tool dispatch — tool
            // cursors (e.g. LineDrawTool crosshair) may have
            // overridden it.
            manager.panCoordinator.refreshPanCursorIfKeyHeld(isStrokeActive);
            if (toolHandled) {
                return true;
            }
        }
        if (view.onMouseMove) {
            return manager.handleEventResult(
                view.onMouseMove(coordinates, buttons, view)
            );
        }
        return false;
    }

    /**
     * Mouse release event triggered by {@link ViewManager}.
     * @param button The button that triggered the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseRelease(
        button: number,
        coordinates: vec2,
        view: View
    ): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;
            this.eventEmitter.emit('onMouseRelease', {
                button,
                coordinates,
                view,
            });

            // Global pan (key+drag release or middle mouse button)
            if (
                manager.panCoordinator.onMouseRelease(
                    button,
                    coordinates,
                    (result) => manager.handleEventResult(result)
                )
            ) {
                return true;
            }

            // Global windowing (right-click-drag release)
            if (manager.windowingCoordinator.onMouseRelease(button)) {
                return true;
            }

            if (manager.activeTool) {
                if (
                    manager.handleEventResult(
                        manager.activeTool.onMouseRelease(
                            button,
                            coordinates,
                            view
                        )
                    )
                ) {
                    return true;
                }
            }
            if (view && view.onMouseRelease) {
                return manager.handleEventResult(
                    view.onMouseRelease(button, coordinates)
                );
            }
            return false;
        });
    }

    /**
     * Mouse wheel event triggered by {@link ViewManager}.
     * @param delta The direction of the MouseWheel event.\
     * -1 if the wheel was scrolled forwards, 1 if the wheel was scrolled backwards.
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param coordinates The mouse position in client pixel coordinates at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool} or the {@link view}.\
     * False otherwise.
     */
    public static onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: vec2,
        view: PlaneView
    ): boolean {
        const manager = this.toolManager;

        // Block all wheel events while the user is actively
        // drawing (mouse held). This prevents zoom, scroll,
        // and brush-size-change mid-stroke.
        const isStroking = manager.activeTool?.isStrokeInProgress() ?? false;
        if (isStroking) {
            return true;
        }

        // 1. Active tool first (custom wheel behaviors like
        //    BrushTool's Shift+Wheel brush-size-change)
        if (manager.activeTool) {
            if (
                manager.handleEventResult(
                    manager.activeTool.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                return true;
            }
        }
        // 2. Global zoom (Ctrl+Wheel)
        if (manager.globalZoomAction) {
            if (
                manager.handleEventResult(
                    manager.globalZoomAction.onMouseWheel(
                        delta,
                        modifierKeys,
                        coordinates,
                        view
                    )
                )
            ) {
                manager.panCoordinator.refreshPanAfterViewChange();
                // Update active tool to new zoom state
                if (manager.activeTool) {
                    manager.activeTool.onViewChanged(coordinates, view);
                }
                return true;
            }
        }
        // 3. Global scroll (slice navigation) — blocked during
        //    any active action (e.g. LineDrawTool between clicks)
        if (
            manager.globalScrollAction &&
            !(manager.activeTool?.isActionInProgress() ?? false)
        ) {
            const result = manager.globalScrollAction.onMouseWheel(
                delta,
                modifierKeys,
                coordinates,
                view
            );
            if (manager.handleEventResult(result)) {
                manager.panCoordinator.refreshPanAfterViewChange();
                // Update tool to new slice coordinates
                if (manager.activeTool) {
                    manager.activeTool.onViewChanged(coordinates, view);
                }
                return true;
            }
        }
        return false;
    }

    /**
     * Mouse double click event triggered by {@link ViewManager}.
     */
    public static onDoubleClick(coordinates: vec2, view: PlaneView): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;
            if (manager.activeTool) {
                if (
                    manager.handleEventResult(
                        manager.activeTool.onMouseDoubleClick(coordinates, view)
                    )
                ) {
                    return true;
                }
            }
            return false;
        });
    }

    public static onMouseLeave() {
        this.withStateNotification(() => {
            const manager = this.toolManager;
            for (const coordinator of manager.coordinators) {
                coordinator.onMouseLeave();
            }
            // Skip tool deactivation while a crosshair drag or a
            // tool action (e.g. transform drag) is in progress.
            // When the mouse moves fast and briefly exits the
            // viewport container, deactivateActiveTool() would
            // cancel the in-progress action and reset state.
            const isToolBusy =
                manager.activeTool?.isActionInProgress() ?? false;
            if (!this._isCrosshairDragActive && !isToolBusy) {
                this.deactivateActiveTool();
            }
        });
    }

    public static onMouseEnter() {
        this.activateActiveTool();
    }

    /**
     * Key press event triggered by {@link ViewManager}.
     * @param key The key that triggered the event
     * @param modifierKeys {@link ModifierKeys} that were active at the time of the event
     * @param view The {@link View} on which the event occured
     * @returns True if the event was successfully handled.\
     * Either by {@link ToolManager.activeAction activeAction}, {@link ToolManager.activeTool activeTool}, {@link view} or {@link ToolManager.sharedKeyEvents sharedKeyEvents}.\
     * False otherwise.
     */
    public static onKeyPress(
        key: string,
        modifierKeys: ModifierKeys,
        view: View
    ): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;

            // Esc cancels active windowing (restores saved WW/WC)
            if (key === 'Escape' && manager.windowingCoordinator.onEscKey()) {
                return true;
            }

            if (manager.activeTool) {
                if (
                    manager.handleEventResult(
                        manager.activeTool.onKeyPress(key, modifierKeys, view)
                    )
                ) {
                    return true;
                }
            }
            // Global scroll (PageUp/Down, ArrowUp/Down) — blocked
            // during any active action (e.g. drawing)
            if (
                manager.globalScrollAction &&
                !(manager.activeTool?.isActionInProgress() ?? false)
            ) {
                if (
                    manager.handleEventResult(
                        manager.globalScrollAction.onKeyPress(
                            key,
                            modifierKeys,
                            view
                        )
                    )
                ) {
                    manager.panCoordinator.refreshPanAfterViewChange();
                    return true;
                }
            }
            if (view && view.onKeyPress) {
                if (
                    manager.handleEventResult(
                        view.onKeyPress(key, modifierKeys)
                    )
                ) {
                    return true;
                }
            }
            return manager.handleEventResult(
                manager.sharedKeyEvents(key, modifierKeys)
            );
        });
    }

    /**
     * Sync Meta/Cmd key state from a mouse event.
     * Bridges the gap when Meta was pressed outside the browser
     * window and no keydown event fired.
     */
    public static syncPanMetaKey(isMetaDown: boolean): void {
        const manager = this.toolManager;
        manager.panCoordinator.syncMetaKeyState(
            isMetaDown,
            () => manager.activeTool?.isStrokeInProgress() ?? false
        );
    }

    /**
     * Track pan-key press at the document level.
     * Called from ViewManager's document-level keydown listener
     * so that Space/Meta pan works regardless of which element
     * has DOM focus.
     * @returns true if the key was consumed (Space).
     */
    public static onPanKeyDown(key: string): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;
            return manager.panCoordinator.onPanKeyDown(
                key,
                () => manager.activeTool?.isStrokeInProgress() ?? false
            );
        });
    }

    /**
     * Track pan-key release at the document level.
     * Called from ViewManager's document-level keyup listener.
     * @returns true if the key was consumed.
     */
    public static onPanKeyUp(key: string): boolean {
        return this.withStateNotification(() => {
            const manager = this.toolManager;
            const consumed = manager.panCoordinator.onPanKeyUp(
                key,
                () => manager.activeTool?.isStrokeInProgress() ?? false
            );
            if (consumed) {
                // Re-activate custom cursor after global pan ends
                manager.activeTool?.refreshCursor();
            }
            return consumed;
        });
    }
}
